import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Brain, Check, GraduationCap, Loader2, Pencil, RefreshCw, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';

const gbp = (pence) => ((pence || 0) / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
const CAT_LABEL = { labour: 'Labour', material: 'Materials', fuel: 'Fuel', plant: 'Plant', other: 'Other' };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB') : '—');

function SupplierRow({ s, api, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name || '');
  const [busy, setBusy] = useState(false);

  const rename = async () => {
    if (name.trim().length < 2 || name.trim() === s.name) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/cost-inbox/memory/${encodeURIComponent(s.key)}`, { supplier_name: name.trim() });
      toast.success('Supplier name corrected'); setEditing(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rename'); }
    finally { setBusy(false); }
  };
  const forgetSite = async (p) => {
    setBusy(true);
    try {
      await api.patch(`/cost-inbox/memory/${encodeURIComponent(s.key)}`, { remove_project_id: p.project_id });
      toast.success(`Forgot ${p.project_ref || 'site'} for ${s.name}`); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not forget site'); }
    finally { setBusy(false); }
  };
  const forget = async () => {
    setBusy(true);
    try {
      await api.delete(`/cost-inbox/memory/${encodeURIComponent(s.key)}`);
      toast.success(`Forgot everything about ${s.name}`); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not forget supplier'); }
    finally { setBusy(false); }
  };
  const removeNote = async (n) => {
    setBusy(true);
    try {
      await api.delete(`/cost-inbox/memory/notes/${n.id}`);
      toast.success('Rule removed'); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove the rule'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-[#1E2530] p-4" data-testid={`memory-supplier-${s.key.replace(/\s+/g, '-')}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          {editing ? (
            <div className="flex items-center gap-2">
              <Input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setEditing(false); }}
                className="bg-[#2A3240] border-white/20 text-white h-8 max-w-xs" autoFocus data-testid="memory-rename-input" />
              <button type="button" onClick={rename} disabled={busy} className="p-1.5 rounded text-emerald-300 hover:bg-emerald-500/10" title="Save" data-testid="memory-rename-save"><Check className="w-4 h-4" /></button>
              <button type="button" onClick={() => { setEditing(false); setName(s.name || ''); }} className="p-1.5 rounded text-gray-400 hover:bg-white/10" title="Cancel"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-white font-semibold" data-testid="memory-supplier-name">{s.name || s.key}</span>
              <button type="button" onClick={() => setEditing(true)} className="p-1 rounded text-gray-500 hover:text-white hover:bg-white/10" title="Correct the spelling" data-testid="memory-rename-btn"><Pencil className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="text-xs text-gray-400 mt-0.5">
            {s.invoices} approved invoice{s.invoices === 1 ? '' : 's'} · {gbp(s.total_pence)} · last {fmtDate(s.last_posted_at)}
          </div>
          {s.aliases?.length > 0 && (
            <div className="text-[11px] text-gray-500 mt-1" data-testid="memory-aliases">AI has also read this supplier as: {s.aliases.map(a => `“${a}”`).join(', ')} → now snaps to “{s.name}”</div>
          )}
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button type="button" disabled={busy} className="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10" title="Forget this supplier" data-testid="memory-forget-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#2A3240] border-white/10 text-white" data-testid="memory-forget-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Forget {s.name}?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-300">
                The inbox will stop using this supplier's past invoices to fill in sites, categories and spelling. Posted ledger rows are not affected. It will start learning again from the next invoice you post.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={forget} className="bg-red-600 hover:bg-red-700 text-white" data-testid="memory-forget-confirm-btn">Forget</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Usual categories</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(s.categories || {}).map(([c, n]) => (
              <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-200">{CAT_LABEL[c] || c} <span className="text-gray-500">×{n}</span></span>
            ))}
            {!Object.keys(s.categories || {}).length && <span className="text-xs text-gray-500">—</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">VAT treatment</div>
          <div className="flex flex-wrap gap-1.5" data-testid="memory-vat-rates">
            {(s.vat_rates || []).map(v => (
              <span key={v.rate} className={`text-[11px] px-2 py-0.5 rounded-full border ${s.vat_default === v.rate ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-white/5 border-white/10 text-gray-200'}`}
                title={v.examples?.length ? `e.g. ${v.examples.map(e => `“${e}”`).join(', ')}` : ''} data-testid={`memory-vat-rate-${String(v.rate).replace('.', '-')}`}>
                {Number(v.rate)}% <span className="opacity-60">×{v.lines}</span>{s.vat_default === v.rate ? <span className="opacity-70"> · usual</span> : ''}
              </span>
            ))}
            {!(s.vat_rates || []).length && <span className="text-xs text-gray-500">not learned yet</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Sites seen before</div>
          <div className="flex flex-wrap gap-1.5" data-testid="memory-sites">
            {(s.projects || []).map(p => (
              <span key={p.project_id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-200">
                {p.project_ref || p.project_id} <span className="text-sky-400/70">×{p.lines}</span>
                <button type="button" onClick={() => forgetSite(p)} disabled={busy} className="ml-0.5 text-sky-300/70 hover:text-red-300" title="Stop suggesting this site" data-testid={`memory-forget-site-${p.project_id}`}><X className="w-3 h-3" /></button>
              </span>
            ))}
            {!(s.projects || []).length && <span className="text-xs text-gray-500">—</span>}
          </div>
        </div>
      </div>
      {s.notes?.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Your rules for this supplier</div>
          <div className="space-y-1.5" data-testid="memory-notes">
            {s.notes.map(n => (
              <div key={n.id} className="flex items-start gap-2 text-[12px] text-amber-100 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5" data-testid={`memory-note-${n.id}`}>
                <GraduationCap className="w-3.5 h-3.5 text-amber-300 mt-0.5 shrink-0" />
                <span className="flex-1">{n.note}</span>
                <button type="button" onClick={() => removeNote(n)} disabled={busy} className="text-amber-300/70 hover:text-red-300" title="Remove this rule" data-testid={`memory-note-remove-${n.id}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {s.descriptions?.length > 0 && (
        <div className="mt-2 text-[11px] text-gray-500 truncate">Typical lines: {s.descriptions.map(d => `“${d}”`).join(' · ')}</div>
      )}
    </div>
  );
}

/** Settings → Inbox memory: what the Cost Inbox AI has learned from posted invoices, per supplier. */
export function InboxMemoryTab({ api }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/cost-inbox/memory'); setSuppliers(r.data.suppliers || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load inbox memory'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-[#2A3240] border-white/10 mb-6" data-testid="inbox-memory-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F5A623]/15 flex items-center justify-center"><Brain className="w-5 h-5 text-[#F5A623]" /></div>
            <div>
              <CardTitle className="text-xl text-white">Cost Inbox memory</CardTitle>
              <CardDescription className="text-gray-400">
                Every invoice you post teaches the reader: the supplier's correct spelling, how their lines are categorised and how they charge VAT (zero-rated vs 20% lines are pre-set next time, still yours to check). You can also teach it a rule while processing an invoice — those appear here. Correct a name, remove a rule, or forget a supplier entirely.
              </CardDescription>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="memory-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && suppliers.length === 0 ? (
          <div className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm" data-testid="memory-empty">Nothing learned yet — post an invoice from the Cost Inbox and its supplier will appear here.</div>
        ) : (
          <div className="space-y-3" data-testid="memory-list">
            {suppliers.map(s => <SupplierRow key={s.key} s={s} api={api} onChanged={load} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
